/* ==========================================================================
   CALI: Mobile search overlay + predictive results (Oct 2026)
   - Opens from any [data-cali-search-open] (header search icon)
   - Auto-focuses the input inside the tap so the iOS keyboard opens
   - Shopify predictive search, rendered by sections/cali-predictive-search.liquid
   - Debounced, cancels stale requests, caches each query for instant repeats
   ========================================================================== */
(function () {
  'use strict';

  if (window.__caliSearchBound) return;
  window.__caliSearchBound = true;

  var SECTION_ID = 'cali-predictive-search';
  var LIMIT = 6;
  var DEBOUNCE_MS = 120;

  var cache = Object.create(null);
  var controller = null;
  var timer = null;
  var lastTrigger = null;

  function normalize(value) {
    return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function getRoot() {
    return document.querySelector('body > [data-cali-search]');
  }

  // Move the overlay to <body> so the header's fixed/transform styles can't trap it.
  function mount() {
    var fresh = document.querySelector('[data-header] [data-cali-search]');
    if (!fresh) return getRoot();
    var old = document.querySelectorAll('body > [data-cali-search]');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
    document.body.appendChild(fresh);
    bind(fresh);
    return fresh;
  }

  function parts(root) {
    return {
      input: root.querySelector('[data-cali-search-input]'),
      hiddenQ: root.querySelector('[data-cali-search-q]'),
      form: root.querySelector('[data-cali-search-form]'),
      results: root.querySelector('[data-cali-search-results]'),
      status: root.querySelector('[data-cali-search-status]'),
      clear: root.querySelector('[data-cali-search-clear]'),
      close: root.querySelector('[data-cali-search-close]')
    };
  }

  function buildUrl(root, q) {
    var base = root.getAttribute('data-suggest-url') || '/search/suggest';
    return base +
      '?q=' + encodeURIComponent(q) +
      '&resources%5Btype%5D=product' +
      '&resources%5Blimit%5D=' + LIMIT +
      '&resources%5Blimit_scope%5D=each' +
      '&resources%5Boptions%5D%5Bunavailable_products%5D=last' +
      '&section_id=' + SECTION_ID;
  }

  function render(root, html) {
    var p = parts(root);
    p.results.innerHTML = html;
    var holder = p.results.querySelector('[data-cali-ps]');
    var count = holder ? parseInt(holder.getAttribute('data-count'), 10) || 0 : 0;
    p.input.setAttribute('aria-expanded', html ? 'true' : 'false');
    p.status.textContent = html ? (count + (count === 1 ? ' result' : ' results')) : '';
  }

  function fallback(raw) {
    var url = '/search?q=' + encodeURIComponent(raw) + '&options%5Bprefix%5D=last';
    return '<div class="cali-ps" data-cali-ps data-count="0"><a class="cali-ps__all" href="' + url + '">View all results</a></div>';
  }

  function search(root, raw) {
    var q = normalize(raw);
    if (controller) { controller.abort(); controller = null; }
    if (!q) { root.classList.remove('is-loading'); render(root, ''); return; }

    if (cache[q] !== undefined) { root.classList.remove('is-loading'); render(root, cache[q]); return; }

    controller = 'AbortController' in window ? new AbortController() : null;
    root.classList.add('is-loading');

    fetch(buildUrl(root, q), {
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Search ' + res.status);
        return res.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var node = doc.querySelector('[data-cali-ps]');
        var html = node ? node.outerHTML : '';
        cache[q] = html;
        // Only paint if the user hasn't typed something else meanwhile
        if (normalize(parts(root).input.value) === q) {
          root.classList.remove('is-loading');
          render(root, html);
        }
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        root.classList.remove('is-loading');
        render(root, fallback(raw.trim()));
      });
  }

  function open(trigger) {
    var root = getRoot() || mount();
    if (!root) return false;
    var p = parts(root);

    lastTrigger = trigger || null;
    root.hidden = false;
    root.classList.add('is-open');
    document.documentElement.classList.add('cali-search-lock');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    // Must run synchronously inside the tap for iOS to show the keyboard
    try { p.input.focus({ preventScroll: true }); } catch (e) { p.input.focus(); }

    if (p.input.value) search(root, p.input.value);
    return true;
  }

  function close() {
    var root = getRoot();
    if (!root || root.hidden) return;
    root.classList.remove('is-open');
    root.hidden = true;
    document.documentElement.classList.remove('cali-search-lock');
    var p = parts(root);
    p.input.blur();
    if (lastTrigger) {
      lastTrigger.setAttribute('aria-expanded', 'false');
      try { lastTrigger.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function bind(root) {
    if (root.__caliBound) return;
    root.__caliBound = true;
    var p = parts(root);

    p.input.addEventListener('input', function () {
      var value = p.input.value;
      p.clear.hidden = !value;
      clearTimeout(timer);
      // Cached queries paint instantly, new ones wait a beat for typing to settle
      if (cache[normalize(value)] !== undefined || !value.trim()) {
        search(root, value);
      } else {
        timer = setTimeout(function () { search(root, value); }, DEBOUNCE_MS);
      }
    });

    p.form.addEventListener('submit', function (event) {
      var value = p.input.value.trim();
      if (!value) { event.preventDefault(); p.input.focus(); return; }
      p.hiddenQ.value = value;
    });

    p.clear.addEventListener('click', function () {
      p.input.value = '';
      p.clear.hidden = true;
      search(root, '');
      p.input.focus();
    });

    p.close.addEventListener('click', close);

    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') { event.preventDefault(); close(); }
    });
  }

  // Delegated so it keeps working after theme-editor section reloads
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('[data-cali-search-open]');
    if (!trigger) return;
    if (open(trigger)) event.preventDefault();
  });

  document.addEventListener('shopify:section:load', function () {
    var wasOpen = getRoot() && !getRoot().hidden;
    if (wasOpen) close();
    mount();
  });

  mount();
})();
